import { authenticatedProApiFetch } from './pro-api.client';

export type SmartApiMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
export type SmartApiMode = 'native' | 'passthrough';

export interface SmartApiCatalogEndpoint {
  id: string;
  group: string;
  method: SmartApiMethod;
  path: string;
  write: boolean;
  mode: SmartApiMode;
  description: string;
  pathParams: string[];
  sampleQuery?: Record<string, string>;
  sampleBody?: unknown;
}

export interface SmartApiCatalog {
  smartApiUrl: string;
  legacyBasePath: string;
  flowTestEnabled: boolean;
  endpoints: SmartApiCatalogEndpoint[];
}

export type SmartApiFlowStepId =
  | 'smart-register'
  | 'claim-pushed'
  | 'legacy-login'
  | 'legacy-token-rejected'
  | 'facade-login'
  | 'token-lifetime'
  | 'onboarding-linked'
  | 'pro-token-accepted'
  | 'cleanup';

export interface SmartApiFlowStep {
  id: SmartApiFlowStepId;
  ok: boolean;
  skipped: boolean;
  status: number | null;
  durationMs: number;
  detail: string | null;
}

export interface SmartApiFlowTestResult {
  email: string;
  ok: boolean;
  steps: SmartApiFlowStep[];
}

export interface SmartApiSideResult {
  url: string;
  status: number | null;
  durationMs: number;
  headers: Record<string, string>;
  body: unknown;
  bodyText: string | null;
  isJson: boolean;
  truncated: boolean;
  error: string | null;
}

export type ShapeType = 'null' | 'boolean' | 'number' | 'string' | 'array' | 'object';

export interface ShapeDifference {
  path: string;
  kind: 'missing' | 'added' | 'type';
  smart?: ShapeType[];
  pro?: ShapeType[];
}

export interface SmartApiCompareResult {
  endpoint: SmartApiCatalogEndpoint;
  request: { method: SmartApiMethod; path: string };
  smart: SmartApiSideResult;
  pro: SmartApiSideResult;
  statusMatch: boolean;
  shapeDiff: ShapeDifference[] | null;
}

export type SmartApiCompareAuth = 'self' | { smartToken: string; proToken: string };

export interface SmartApiCompareRequest {
  endpointId: string;
  pathParams: Record<string, string>;
  query: Record<string, string>;
  body?: unknown;
  auth: SmartApiCompareAuth;
  confirmWrite?: boolean;
}

export interface SmartApiSessionSide {
  status: number | null;
  token: string | null;
  error: string | null;
}

export interface SmartApiTestSession {
  smart: SmartApiSessionSide;
  pro: SmartApiSessionSide;
}

export function fetchSmartApiCatalog(accessToken: string): Promise<SmartApiCatalog> {
  return authenticatedProApiFetch<SmartApiCatalog>('/system/smart-api/endpoints', accessToken);
}

export function compareSmartApiEndpoint(
  accessToken: string,
  request: SmartApiCompareRequest,
): Promise<SmartApiCompareResult> {
  return authenticatedProApiFetch<SmartApiCompareResult>('/system/smart-api/compare', accessToken, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function runSmartApiFlowTest(accessToken: string): Promise<SmartApiFlowTestResult> {
  return authenticatedProApiFetch<SmartApiFlowTestResult>('/system/smart-api/flow-test', accessToken, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function createSmartApiTestSession(
  accessToken: string,
  credentials: { email: string; password: string },
): Promise<SmartApiTestSession> {
  return authenticatedProApiFetch<SmartApiTestSession>('/system/smart-api/sessions', accessToken, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}
